import "./style.css";
import { Game } from "./app/game";
import { loadMap, showMapError } from "./app/mapLoad";
import { fetchMapIndex, randomMapId, realMapsEnabled, takeWelcome, type MapIndexEntry } from "./app/mapPicker";
import { parseParams } from "./app/params";

const canvas = document.getElementById("canvas") as HTMLCanvasElement;
const hudRoot = document.getElementById("hud-root") as HTMLElement;
const joystickZone = document.getElementById("joystick-zone") as HTMLElement;

/**
 * Two ways to arrive at the welcome card.
 *
 * A BARE visit — no query at all — gets a random scenario map and a card
 * naming it. Deliberately only a bare visit: every other entry point means
 * somebody asked for something specific (a researcher's
 * `?level=research&seed=7`, a shared link to one map, a scripted recording,
 * the e2e suite), and randomising those would silently replace what they
 * asked for.
 *
 * A REROLL from the card lands on an ordinary `?map=<id>` URL, which is not a
 * bare visit but should still be introduced rather than dropping the player
 * straight in. That one carries its signal in sessionStorage, so the URL a
 * player might share stays clean.
 */
const rerolled = takeWelcome();
/**
 * Diagnostic-only flags do not count as "asking for something specific".
 *
 * `?perf=1` is there to diagnose the ordinary first-visit experience, so
 * suppressing the card and the random map — the very path being diagnosed —
 * would make the flag useless for the one job it has.
 */
/**
 * `?real=1` joins them: it widens which maps a first visit may land on, it does not
 * name one. Treating it as "asking for something specific" would send a visitor who
 * switched the real streets on to the built-in district instead of any of them.
 */
const DIAGNOSTIC_ONLY = new Set(["perf", "debug", "real"]);
const bareVisit = [...new URLSearchParams(window.location.search).keys()].every((k) =>
  DIAGNOSTIC_ONLY.has(k),
);

// The index is wanted either way: the end panel offers the next map in it, and
// the picker lists it. Failing to fetch it returns [] and simply means no
// random pick and no sequence — never a broken game.
const index: MapIndexEntry[] = await fetchMapIndex();

let mapId = parseParams(window.location.search).mapId;
let welcome: MapIndexEntry | null = null;

if (rerolled && mapId) {
  welcome = index.find((e) => e.id === mapId) ?? null;
} else if (bareVisit) {
  // Real-street reconstructions are opt-in (?real=1): a first visit should open
  // in a moment, and each of those maps is tens of megabytes of Gaussians.
  const pick = randomMapId(index, Math.random, undefined, realMapsEnabled(window.location.search));
  if (pick !== null) {
    mapId = pick;
    welcome = index.find((e) => e.id === pick) ?? null;
    // Put the choice in the URL so the run is reproducible and shareable, and
    // so a reload replays the same map instead of rerolling. replaceState, not
    // a redirect: no reload, and the back button still leaves the site.
    // Keep any diagnostic flag: the point of ?perf=1 is to survive the reload
    // it triggers.
    const q = new URLSearchParams(window.location.search);
    q.set("map", pick);
    window.history.replaceState(null, "", `?${q.toString()}`);
  }
}

// A custom map (?map=) is fetched and validated before the game starts. If it
// will not load we say so rather than silently dropping the player into the
// built-in district — they asked for a particular map.
let game: Game | null = null;
if (mapId) {
  const result = await loadMap(mapId);
  if (result.doc) game = new Game(canvas, hudRoot, joystickZone, result.doc);
  else showMapError(hudRoot, result.error ?? "That map could not be loaded.");
} else {
  game = new Game(canvas, hudRoot, joystickZone);
}

if (game) {
  game.setMapIndex(index);
  if (welcome) {
    // Hold the clock while the card is up: the attempt runs in real time and
    // times out, so reading the card must not cost the player their budget.
    game.showWelcome(welcome.name, welcome.description);
  }
}
